import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Grid,
  Button,
  ClickAwayListener,
  TablePagination,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Drawer,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Badge,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FilterListIcon from "@mui/icons-material/FilterList";
import axios from "axios";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./LeadsTable.css";
import {
  Bell,
  BellOff,
  Delete,
  Download,
  Eye,
  SquarePen,
  Users,
  WalletCards,
} from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import { exportLeadsToExcel } from "../../../components/exportLeadsToExcel";
import { UserComponentRegistry } from "ag-grid-community";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { formatDate } from "../../../utils/formatters";

const getStatusColors = (status) => {
  const currentStatus = status?.toLowerCase() || "";

  switch (currentStatus) {
    case "paid":
      return { bg: "#5e8c6f", text: "#ffffff", border: "#166534" };
    case "inactive":
      return { bg: "#e27272", text: "#ffffff", border: "#dc2626" };
    case "yettostart":
      return { bg: "#5d80ae", text: "#ffffff", border: "#1e40af" };
    default:
      return { bg: "#f3f4f6", text: "#4b5563", border: "#9ca3af" };
  }
};

const LeadsTable = () => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Filter states
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedSubscriptionStatus, setSelectedSubscriptionStatus] = useState(
    []
  );
  const [emailFilter, setEmailFilter] = useState("");
  const [didFilter, setDidFilter] = useState("");

  // NEW: Replace expiryDateFilter with expiryDateRange
  const [expiryDateRange, setExpiryDateRange] = useState([]);
  const [expiryCalendarVisible, setExpiryCalendarVisible] = useState(false);

  const [admins, setAdmins] = useState([]);

  const role = localStorage.getItem("role");
  const adminId = localStorage.getItem("adminId");

  // Available options
  const countries = ["IND", "UAE", "USA"];
  const subscriptionStatuses = ["paid", "demo", "yettostart", "inactive"];

  useEffect(() => {
    getAllUsers();
    getAllAdmins();
  }, [
    selectedCountries,
    selectedSubscriptionStatus,
    emailFilter,
    didFilter,
    selectedDateRange,
    expiryDateRange,
  ]);

  const getAllAdmins = async () => {
    try {
      const res = await axiosInstance.get(apiEndPointsConfig.getAllAdmins);
      const adminsList = res.data?.admins || [];
      // Sort admins by name for easier selection
      const sortedAdmins = [...adminsList].sort((a, b) =>
        (a.fullName || "").localeCompare(b.fullName || "")
      );
      setAdmins(sortedAdmins);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const getAllUsers = async () => {
    try {
      const params = {};

      if (selectedCountries.length > 0) {
        params.country = selectedCountries.join(",");
      }

      if (selectedSubscriptionStatus.length > 0) {
        params.subscriptionStatus = selectedSubscriptionStatus.join(",");
      }

      if (emailFilter.trim()) {
        params.email = emailFilter.trim();
      }

      if (didFilter.trim()) {
        params.did = didFilter.trim();
      }

      if (selectedDateRange.length === 2) {
        params.startDate = selectedDateRange[0].toISOString();
        params.endDate = selectedDateRange[1].toISOString();
      }

      // NEW: Updated to send custom expiry date range
      if (expiryDateRange.length === 2) {
        params.expiryStartDate = expiryDateRange[0].toISOString();
        params.expiryEndDate = expiryDateRange[1].toISOString();
      }

      const res = await axiosInstance.get(apiEndPointsConfig?.listOfLeads, {
        params,
      });

      setData(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  const getPlanColor = (planType) => {
    const colors = {
      Admin: { bg: "#f3e5f5", text: "#7b1fa2", border: "#ce93d8" },
      Demo: { bg: "#e3f2fd", text: "#1565c0", border: "#90caf9" },
      "Drop Off": { bg: "#ffebee", text: "#c62828", border: "#ef9a9a" },
      "Growth Plan": { bg: "#e8f5e9", text: "#2e7d32", border: "#81c784" },
      "Start-Up Plan": { bg: "#e1f5fe", text: "#0277bd", border: "#4fc3f7" },
      "Freemium Plan": { bg: "#fff3e0", text: "#e65100", border: "#ffb74d" },
      default: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
    };
    return colors[planType] || colors.default;
  };

  const handleCalendarToggle = () => setCalendarVisible((prev) => !prev);

  const clearDates = (e) => {
    e.stopPropagation();
    setSelectedDateRange([]);
  };

  // NEW: Clear expiry dates
  const clearExpiryDates = (e) => {
    e.stopPropagation();
    setExpiryDateRange([]);
  };

  const onCalendarChange = (value) => {
    if (Array.isArray(value)) {
      setSelectedDateRange(value);
      setCalendarVisible(false);
    }
  };

  // NEW: Handle expiry calendar change
  const onExpiryCalendarChange = (value) => {
    if (Array.isArray(value)) {
      setExpiryDateRange(value);
      setExpiryCalendarVisible(false);
    }
  };

  // Client-side search filter
  const filteredData = data.filter((row) => {
    if (!searchQuery) return true;

    const fullText = `
      ${row.name} ${row.email} ${row.phoneNumber}
      ${row.companyName} ${row.country} ${row.customerCareNo} ${row.dropoff}
    `.toLowerCase();

    return fullText.includes(searchQuery.toLowerCase());
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Stats calculations
  const totalLeads = data.length;
  const paidUsers = data.filter((d) => d.subscriptionStatus === "paid").length;
  const yetToStart = data.filter(
    (d) => d.subscriptionStatus === "yettostart"
  ).length;
  const activeLeads = filteredData.length;

  const stats = [
    {
      label: "Total Leads",
      value: totalLeads,
      icon: <PeopleIcon />,
      color: "#3b82f6",
      bgColor: "#eff6ff",
    },
    {
      label: "Filtered Results",
      value: activeLeads,
      icon: <FilterListIcon />,
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
    },
    {
      label: "Active Users",
      value: paidUsers,
      icon: <CheckCircleIcon />,
      color: "#10b981",
      bgColor: "#ecfdf5",
    },
    {
      label: "Yet To Start",
      value: yetToStart,
      icon: <PauseCircleOutlineIcon />,
      color: "#f52a0b",
      bgColor: "#f6edeb",
    },
  ];

  const handleAssignManager = async (leadId, newAdminId) => {
    try {
      const response = await axiosInstance.post(
        apiEndPointsConfig.assignAccountManager,
        { leadId, adminId: newAdminId }
      );

      // alert("Assigned successfully");
      await getAllUsers();
    } catch (error) {
      console.error(error);
      alert("Failed to assign");
    }
  };

  const handleToggleRenewalReminder = async (leadId, nextEnabled) => {
    const confirmed = window.confirm(
      nextEnabled
        ? "Are you sure you want to enable renewal reminders for this lead?"
        : "Are you sure you want to disable renewal reminders for this lead?"
    );
    if (!confirmed) return;

    try {
      await axiosInstance.post(apiEndPointsConfig.toggleRenewalReminder, {
        leadId,
        enabled: nextEnabled,
      });
      setData((prev) =>
        prev.map((row) =>
          row._id === leadId
            ? { ...row, renewalReminderEnabled: nextEnabled }
            : row
        )
      );
    } catch (error) {
      console.error("Error toggling renewal reminder:", error);
      alert("Failed to update renewal reminder");
    }
  };

  // Superadmin-only: swap the admin's own JWT for a real user-scoped one so
  // /dashboard genuinely authenticates as this lead, instead of just setting
  // a "userId" localStorage key nothing actually reads for auth.
  const handleLoginAsUser = async (leadId) => {
    try {
      const res = await axiosInstance.post(
        `${apiEndPointsConfig.loginAsUser}/${leadId}`
      );
      const { token } = res.data;
      if (!token) throw new Error("No token returned");

      // Stash the admin's own session so "Switch to Admin" can restore it
      const currentToken = localStorage.getItem("token");
      if (currentToken) {
        localStorage.setItem("adminToken", currentToken);
      }

      localStorage.setItem("token", token);
      localStorage.setItem("userId", leadId);
      navigate(`/dashboard`);
    } catch (error) {
      console.error("Error logging in as user:", error);
      alert(
        error?.response?.data?.message || "Failed to log in as this user"
      );
    }
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedCountries([]);
    setSelectedSubscriptionStatus([]);
    setEmailFilter("");
    setDidFilter("");
    setSelectedDateRange([]);
    setExpiryDateRange([]); // NEW: Clear expiry date range
    setPage(0);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedCountries.length > 0) count++;
    if (selectedSubscriptionStatus.length > 0) count++;
    if (emailFilter.trim()) count++;
    if (didFilter.trim()) count++;
    if (selectedDateRange.length === 2) count++;
    if (expiryDateRange.length === 2) count++; // NEW: Count expiry date range
    return count;
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100vh",
        py: 2,
        px: { xs: 2, md: 3 },
      }}
    >
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: "16px",
                background: "white",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: stat.bgColor,
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: stat.color,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search and Filter Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: "12px",
          background: "white",
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Grid item xs={12} sm={8} md={9}>
            <TextField
              label="Search by name, email, phone, company..."
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "10px",
                  backgroundColor: "#f9fafb",
                  "&:hover": { backgroundColor: "#f3f4f6" },
                  "&.Mui-focused": {
                    backgroundColor: "white",
                    boxShadow: "0 0 0 2px rgba(59,130,246,0.15)",
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            md={3}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download size={18} />}
              onClick={() => exportLeadsToExcel(filteredData)}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              Export Leads
            </Button>
            <Badge badgeContent={getActiveFilterCount()} color="primary">
              <Button
                fullWidth
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  borderRadius: "10px",
                  height: 40,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  background: "#6366F1",
                }}
              >
                Filters
              </Button>
            </Badge>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            background: "#FAF9F5",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: "#1f2937" }}
            >
              Quick Filters
            </Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Date Range Filter for Lead Creation Date */}

          <Box sx={{ position: "relative", mb: 2 }}>
            <Box
              sx={{
                border:
                  selectedDateRange?.length === 2
                    ? "2px solid #3b82f6"
                    : "1px solid #d1d5db",
                borderRadius: "10px",
                height: 56,
                px: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor:
                  selectedDateRange?.length === 2 ? "#eff6ff" : "white",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor:
                    selectedDateRange?.length === 2 ? "#dbeafe" : "#f9fafb",
                },
              }}
              onClick={handleCalendarToggle}
            >
              <CalendarMonthIcon
                sx={{
                  fontSize: 24,
                  color:
                    selectedDateRange?.length === 2 ? "#3b82f6" : "#6b7280",
                }}
              />

              <Typography
                sx={{
                  flex: 1,
                  color:
                    selectedDateRange?.length === 2 ? "#3b82f6" : "#6b7280",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {selectedDateRange?.length === 2
                  ? `${selectedDateRange[0]?.toLocaleDateString()} - ${selectedDateRange[1]?.toLocaleDateString()}`
                  : "Select Date Range"}
              </Typography>

              {selectedDateRange?.length === 2 && (
                <IconButton size="small" onClick={clearDates}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {calendarVisible && (
              <ClickAwayListener onClickAway={() => setCalendarVisible(false)}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "60px",
                    left: 0,
                    right: 0,
                    zIndex: 99,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <Calendar
                    selectRange
                    onChange={onCalendarChange}
                    value={selectedDateRange}
                    maxDate={new Date()}
                  />
                </Box>
              </ClickAwayListener>
            )}
          </Box>

          {/* Country Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Country</InputLabel>
            <Select
              multiple
              value={selectedCountries}
              onChange={(e) => setSelectedCountries(e.target.value)}
              input={<OutlinedInput label="Country" />}
              renderValue={(selected) => selected.join(", ")}
              sx={{
                borderRadius: "10px",
                backgroundColor: "white",
              }}
            >
              {countries.map((country) => (
                <MenuItem key={country} value={country}>
                  <Checkbox checked={selectedCountries.indexOf(country) > -1} />
                  <ListItemText primary={country} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Subscription Status Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Subscription Status</InputLabel>
            <Select
              multiple
              value={selectedSubscriptionStatus}
              onChange={(e) => setSelectedSubscriptionStatus(e.target.value)}
              input={<OutlinedInput label="Subscription Status" />}
              renderValue={(selected) => selected.join(", ")}
              sx={{
                borderRadius: "10px",
                backgroundColor: "white",
              }}
            >
              {subscriptionStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox
                    checked={selectedSubscriptionStatus.indexOf(status) > -1}
                  />
                  <ListItemText
                    primary={status.charAt(0).toUpperCase() + status.slice(1)}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* NEW: Expiry Date Range Filter - Replaces the dropdown */}
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600, color: "#374151" }}
          >
            Account Expiry Date
          </Typography>
          <Box sx={{ position: "relative", mb: 2 }}>
            <Box
              sx={{
                border:
                  expiryDateRange?.length === 2
                    ? "2px solid #3b82f6"
                    : "1px solid #d1d5db",
                borderRadius: "10px",
                height: 56,
                px: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor:
                  expiryDateRange?.length === 2 ? "#eff6ff" : "white",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor:
                    expiryDateRange?.length === 2 ? "#dbeafe" : "#f9fafb",
                },
              }}
              onClick={() => setExpiryCalendarVisible(true)}
            >
              <CalendarMonthIcon
                sx={{
                  fontSize: 24,
                  color: expiryDateRange?.length === 2 ? "#3b82f6" : "#6b7280",
                }}
              />

              <Typography
                sx={{
                  flex: 1,
                  color: expiryDateRange?.length === 2 ? "#3b82f6" : "#6b7280",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {expiryDateRange?.length === 2
                  ? `${expiryDateRange[0]?.toLocaleDateString()} - ${expiryDateRange[1]?.toLocaleDateString()}`
                  : "Select Expiry Date Range"}
              </Typography>

              {expiryDateRange?.length === 2 && (
                <IconButton size="small" onClick={clearExpiryDates}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {expiryCalendarVisible && (
              <ClickAwayListener
                onClickAway={() => setExpiryCalendarVisible(false)}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "60px",
                    left: 0,
                    right: 0,
                    zIndex: 99,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <Calendar
                    selectRange
                    onChange={onExpiryCalendarChange}
                    value={expiryDateRange}
                    maxDate={
                      new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() + 12,
                        new Date().getDate()
                      )
                    }
                  />
                </Box>
              </ClickAwayListener>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearAllFilters}
              sx={{
                borderRadius: "10px",
                height: 44,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#ef4444",
                color: "#ef4444",
                "&:hover": {
                  borderColor: "#dc2626",
                  backgroundColor: "#fef2f2",
                },
              }}
            >
              Clear All
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{
                borderRadius: "10px",
                height: 44,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
        }}
      >
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                {[
                  "S No",
                  "Lead Name",
                  "Email / Mobile",
                  "Company Name",
                  "Country",
                  "DID No",
                  "Plan",
                  "Drop Off",
                  "Created",
                  "Expiry",
                  "Manager",
                  "Action",
                ].map((header) => {
                  const isManager = header === "Manager";
                  const isAction = header === "Action";

                  return (
                    <TableCell
                      key={header}
                      sx={{
                        minWidth: header === "S No" ? 55 : 120,
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        py: 1,
                        textAlign: isAction ? "center" : "left",
                        position: isManager || isAction ? "sticky" : "static",
                        right: isAction ? 0 : isManager ? 120 : "auto",
                        zIndex: isAction || isManager ? 5 : 1,
                        background: "#5e60cfff",
                      }}
                    >
                      {header}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, index) => {
                  const createdDate = new Date(row?.createdAt);
                  const daysToAdd = row?.country === "UAE" ? 3 : 30;
                  const expiryDate = new Date(createdDate);
                  expiryDate.setDate(createdDate.getDate() + daysToAdd);

                  const planColors = getPlanColor(row.dropoff);
                  const isPaid = row.subscriptionStatus === "paid";

                  return (
                    <TableRow
                      key={row._id}
                      hover
                      sx={{
                        transition: "0.15s",
                        "&:hover": {
                          backgroundColor: "#f4f4f5",
                        },
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <TableCell
                        sx={{ py: 0.8, px: 0.6, fontWeight: 500, pl: 2 }}
                      >
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      <TableCell sx={{ py: 0.8, px: 0.6, pl: 2 }}>
                        <Typography fontSize="0.8rem" sx={{ fontWeight: 500 }}>
                          {row.name}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{ py: 0.8, px: 0.6, color: "#6b7280", pl: 2 }}
                      >
                        {row.email ?? row.phoneNumber}
                      </TableCell>

                      <TableCell
                        sx={{ py: 0.8, px: 0.6, fontWeight: 500, pl: 2 }}
                      >
                        {row.companyName}
                      </TableCell>

                      <TableCell sx={{ py: 0.8, px: 0.6, pl: 2 }}>
                        <Chip
                          label={row.country}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            height: 22,
                            fontSize: "0.7rem",
                            color: row.country == "IND" ? "white" : "black",
                            backgroundColor:
                              row.country == "UAE" ? "" : "#6ba165",
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{ py: 0.8, px: 0.6, color: "#6b7280", pl: 2 }}
                      >
                        {row.customerCareNo}
                      </TableCell>


                      {/* Subscription Amount */}
                      <TableCell
                        sx={{ py: 0.8, px: 0.6, fontWeight: 500, pl: 2, color: "#4b5563" }}
                      >
                        {row.subscriptionAmount !== undefined ? row.subscriptionAmount : 0} {row?.country === "UAE" ? "AED" : ""}
                      </TableCell>

                      <TableCell sx={{ py: 0.8, px: 0.6, pl: 2 }}>
                        {(() => {
                          const colors = getStatusColors(
                            row.subscriptionStatus
                          );

                          return (
                            <Chip
                              label={
                                row.dropoff === "demo"
                                  ? row.subscriptionStatus
                                  : row.dropoff
                              }
                              size="small"
                              sx={{
                                fontWeight: 600,
                                height: 22,
                                fontSize: "0.7rem",
                                borderRadius: "20px",
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.border}`,
                              }}
                            />
                          );
                        })()}
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 0.8,
                          px: 0.6,
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          pl: 2,
                        }}
                      >
                        {formatDate(row?.createdAt)}
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 0.8,
                          px: 0.6,
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          pl: 2,
                        }}
                      >
                        {row.expiryDate
                          ? formatDate(row.expiryDate)
                          : formatDate(expiryDate)}
                      </TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 120,
                          background: "white",
                          zIndex: 4,
                          py: 0.8,
                          px: 0.6,
                          minWidth: 160
                        }}
                      >
                        <Autocomplete
                          size="small"
                          disabled={role !== "superadmin"}
                          options={admins}
                          getOptionLabel={(option) => option.fullName || ""}
                          value={admins.find(adm => adm._id === row.accountManager?._id) || null}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              handleAssignManager(row._id, newValue._id);
                            } else {
                              handleAssignManager(row._id, null);
                            }
                          }}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          PaperProps={{
                            elevation: 8,
                            sx: {
                              borderRadius: "12px",
                              mt: 1,
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                py: 1,
                                "&:hover": {
                                  backgroundColor: "#f5f6f6",
                                  color: "#725AB7",
                                }
                              }
                            }
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiDisabled": {
                              opacity: 0.9,
                              "-webkit-text-fill-color": "unset",
                            },
                            "& .MuiAutocomplete-endAdornment": {
                              display: role !== "superadmin" ? "none" : "flex",
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Assign"
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: 30,
                                  fontSize: "0.68rem",
                                  borderRadius: "20px",
                                  fontWeight: 600,
                                  backgroundColor: row.accountManager ? "#f0f2ff" : "#fff5f5",
                                  color: row.accountManager ? "#4f46e5" : "#e03131",
                                  paddingRight: "28px !important",
                                  transition: "all 0.2s",
                                  "& fieldset": {
                                    borderColor: row.accountManager ? "#c7d2fe" : "#ffc9c9",
                                    borderWidth: "1px !important",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: row.accountManager ? "#818cf8" : "#faadad",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#6366f1",
                                    boxShadow: "0 0 0 2px rgba(99,102,241,0.1)",
                                  },
                                },
                                "& .MuiInputBase-input::placeholder": {
                                  color: "#e03131",
                                  opacity: 0.7,
                                }
                              }}
                            />
                          )}
                        />
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          right: 0,
                          background: "white",
                          zIndex: 5,
                          py: 0.8,
                          px: 0.6,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Details" arrow>
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(`/admin/single-lead/${row._id}`)
                              }
                            >
                              <Eye size={16} />
                            </IconButton>
                          </Tooltip>

                          {role !== "viewer" && role !== "agent" && (
                            <>

                              {(role === "superadmin" ||
                                role === "admin" ||
                                adminId === row.accountManager?._id) && (
                                  <>
                                    <Tooltip title="Add Payment" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          navigate(`/admin/revenue/${row._id}`)
                                        }
                                      >
                                        <WalletCards size={16} />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Edit Lead" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          navigate(`/admin/edit-user/${row._id}`)
                                        }
                                      >
                                        <SquarePen size={16} />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip
                                      title={
                                        row.renewalReminderEnabled === true
                                          ? "Renewal Reminders Enabled — click to disable"
                                          : "Renewal Reminders Disabled — click to enable"
                                      }
                                      arrow
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleToggleRenewalReminder(
                                            row._id,
                                            row.renewalReminderEnabled !== true
                                          )
                                        }
                                      >
                                        {row.renewalReminderEnabled === true ? (
                                          <Bell size={16} color="#10b981" />
                                        ) : (
                                          <BellOff size={16} color="#9ca3af" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    {role === "superadmin" &&
                                      row.dropoff == "demo" && (
                                        <Tooltip title="Login as this user" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleLoginAsUser(row._id)
                                            }
                                          >
                                            <AdminPanelSettingsIcon />
                                          </IconButton>
                                        </Tooltip>
                                      )}

                                    {row.subscriptionStatus == "paid" ? (
                                      <>
                                        <Tooltip title="Add Team Member" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              navigate(
                                                `/admin/add-team-member/${row._id}`
                                              )
                                            }
                                          >
                                            <Users size={16} />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </>
                                )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <Typography>No matching leads found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[50, 100, 150]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default LeadsTable;
